import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatErr } from "@/lib/api";
import { formatEta } from "@/components/UploadPanel";

export const QueueStatus = ({ eventId, pingKey, onReadyChange, uploading = false }) => {
  const [q, setQ] = useState(null);
  const [rate, setRate] = useState(0);
  const lastReady = useRef(null);
  const sample = useRef(null);
  const uploadingRef = useRef(uploading);
  useEffect(() => { uploadingRef.current = uploading; }, [uploading]);

  const poll = useCallback(async () => {
    const { data } = await api.get(`/events/${eventId}/queue`);
    setQ(data);
    const t = Date.now();
    if (sample.current && data.ready > sample.current.ready) setRate((data.ready - sample.current.ready) / ((t - sample.current.t) / 1000));
    if (!sample.current || data.ready !== sample.current.ready) sample.current = { ready: data.ready, t };
    if (lastReady.current !== null && data.ready !== lastReady.current) onReadyChange?.();
    lastReady.current = data.ready;
    return data;
  }, [eventId, onReadyChange]);

  useEffect(() => {
    let stop = false;
    let timer;
    const loop = async () => {
      const d = await poll().catch(() => null);
      if (!stop && (uploadingRef.current || (d && d.queued + d.working > 0))) timer = setTimeout(loop, 3000);
    };
    loop();
    return () => { stop = true; clearTimeout(timer); };
  }, [poll, pingKey]);


  const retry = async () => {
    try {
      const { data } = await api.post(`/events/${eventId}/queue/retry`);
      toast.success(`${data.retried} foto dimasukkan ulang ke antrean`);
      poll();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  if (!q) return null;
  const pending = q.queued + q.working;
  if (!pending && !q.failed) return null;
  const totalBatch = pending + q.ready;

  return (
    <div className="mb-8 rounded-2xl bg-white border border-zinc-200 p-5" data-testid="queue-status">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm font-medium flex items-center gap-2" data-testid="queue-status-text">
          {pending ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {pending ? `${pending} foto sudah ber-watermark, sedang deteksi wajah di server` : "Semua foto selesai diproses"}
        </p>
        {pending > 0 && <span className="text-xs text-zinc-500" data-testid="queue-eta">{rate > 0 ? `${rate.toFixed(1)} foto/dtk · selesai ± ${formatEta(pending / rate)} · ` : ""}{uploading ? "Proses berjalan bersamaan dengan unggahan" : "Tab boleh ditutup, proses tetap berjalan"}</span>}
      </div>
      {pending > 0 && <Progress value={(q.ready / Math.max(totalBatch, 1)) * 100} />}
      {q.failed > 0 && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="queue-failed">
          <div className="flex items-center justify-between gap-3">
            <p className="text-amber-800"><AlertTriangle className="inline h-4 w-4 mr-1" />{q.failed} foto gagal diproses</p>
            <Button size="sm" className="rounded-lg bg-amber-600 hover:bg-amber-700" onClick={retry} data-testid="queue-retry-button"><RotateCcw className="mr-1 h-3.5 w-3.5" />Proses ulang</Button>
          </div>
          <ul className="mt-2 max-h-24 overflow-y-auto text-xs text-amber-900">
            {q.failed_items.map((f) => <li key={f.id} className="truncate">{f.filename}: {f.error || "-"}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};
